import * as THREE from 'three';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { ENV, GAU8, AIM9, AGM65, SMOKE_TRAILS } from '../core/SimConfig.js';
import { gaussRand, DEG2RAD } from '../utils.js';
import AIM9Missile, { MissileState } from './AIM9Missile.js';
import AGM65Missile from './AGM65Missile.js';

export const WeaponMode = Object.freeze({
  NONE: 0,
  GAU8: 1,
  AIM9: 2,
  AGM65: 3,
});

const WEAPON_LABELS = ['NONE', 'GAU-8 30mm Cannon', 'AIM-9M Missile', 'AGM-65 Maverick'];
const WEAPON_MODE_COUNT = WEAPON_LABELS.length;

// ── Reusable temp vectors (zero-alloc hot path) ───────────────────────────
const _gunWorldPos = new THREE.Vector3();
const _gunWorldDir = new THREE.Vector3();
const _quat = new THREE.Quaternion();
const _targetPos = new THREE.Vector3();
const _toTarget = new THREE.Vector3();
const _segDelta = new THREE.Vector3();

// Drag pre-computed constant: 0.5 * airDensity * Cd * A / mass
const DRAG_K = 0.5 * ENV.airDensity * GAU8.bulletDragCd * GAU8.bulletArea / GAU8.bulletMass;

// Dispersion: 80th-percentile Rayleigh radius = 1.794σ
const DISPERSION_SIGMA = GAU8.dispersion / 1.7941;

export default class WeaponSystem {
  /**
   * @param {{
   *   model: THREE.Object3D,
   *   scene: THREE.Scene,
   *   aim9Model?: THREE.Object3D,
   *   gau8?: 'none' | 'full',
   *   unlimitedAmmo?: boolean,
   * }} opts
   */
  constructor({ model, scene, aim9Model = null, aim9Count = 0, agm65Model = null, agm65Count = 0, gau8 = 'full', unlimitedAmmo = false, particleSystem = null, smokeTrailSystem = null }) {
    this.mode = WeaponMode.NONE;
    this.firing = false;

    this.scene = scene;
    this.aircraftModel = model;
    this._aim9Model = aim9Model;
    this._agm65Model = agm65Model;
    this.particleSystem = particleSystem;
    this.smokeTrailSystem = smokeTrailSystem;
    // 'none' loadout flies with an empty gun; 'full' (or unspecified) gets a full drum.
    this.ammo = gau8 === 'none' ? 0 : GAU8.ammoCapacity;
    // When true, the gun never depletes (set from the start-screen loadout).
    this.unlimitedAmmo = unlimitedAmmo;

    // Locate the Gun empty node in the A-10 model
    this.gunNode = model.getObjectByName('Gun');
    if (!this.gunNode) {
      console.warn('WeaponSystem: "Gun" node not found in model');
    }

    // ── AIM-9 mounting ───────────────────────────────────────────────
    /** @type {AIM9Missile[]} */
    this._missiles = [];
    this._missileFireAccum = 0;
    this.trackedTarget = null;
    this.armedAIM9 = false;

    // Initial loadout comes from the start-screen armament selection.
    this.setAIM9Count(aim9Count);

    // ── AGM-65 mounting ──────────────────────────────────────────────
    /** @type {AGM65Missile[]} */
    this._mavericks = [];
    this._maverickFireAccum = 0;
    this.setAGM65Count(agm65Count);

    // ── Fire-rate accumulator ─────────────────────────────────────────
    this._fireAccum = 0;

    // ── Spin-up state ────────────────────────────────────────────────
    // 0 = at fireRateStart, 1 = at fireRateMax. Ramps up while firing,
    // decays back down symmetrically when not firing.
    this._spinup = 0;

    // ── Struct-of-Arrays bullet pool ──────────────────────────────────
    const N = GAU8.poolSize;
    this.poolSize = N;
    this.aliveCount = 0;

    this.posX = new Float32Array(N);
    this.posY = new Float32Array(N);
    this.posZ = new Float32Array(N);
    this.velX = new Float32Array(N);
    this.velY = new Float32Array(N);
    this.velZ = new Float32Array(N);
    this.age  = new Float32Array(N);
    this.alive = new Uint8Array(N);   // 0 = dead, 1 = alive

    // ── Debug trail pool ──────────────────────────────────────────────
    // Each slot gets a Line + BufferGeometry, lazily created, recycled on death.
    this._trailLines = new Array(N).fill(null);    // THREE.Line | null
    this._trailCounts = new Uint32Array(N);        // how many points written
    this._trailMaterial = new THREE.LineBasicMaterial({ color: 0xff4400 });
    this._debugActive = false;

    // ── Impact positions (for future hit effects) ─────────────────────
    this._impacts = [];   // { x, y, z } — drained by consumer each frame

    // ── Muzzle flash events (drained by App → ParticleSystem) ────────
    this._muzzleFlashes = []; // { x, y, z, vx, vy, vz, dx, dy, dz }

    // ── Missile launch events (drained by AudioManager) ──────────────
    this._missileLaunches = []; // { x, y, z }

    // ── Missile detonation events (drained by AudioManager) ──────────
    this._missileExplosions = []; // { name, x, y, z }

    // ── Hit targets ───────────────────────────────────────────────────
    // Array of entities exposing `.hitVolume` (a HitVolume instance) and
    // optionally `.onProjectileHit(x, y, z, damage, source)`, where `source`
    // names the weapon ('gau8' / 'aim9' / 'agm65'). Set externally by App when a
    // scenario starts. Same array is reused for missiles and future weapons.
    this._targets = null;

    // Optional MissionStats sink (combat scenarios). When set, the gun + guided
    // missiles report rounds fired and hits/misses for the end-of-mission score.
    this.stats = null;
  }

  /** Provide the array of targetable entities. Pass null to clear. */
  setTargets(entities) {
    this._targets = entities;
  }

  /**
   * Set the mounted AIM-9 loadout to exactly `count` missiles (clamped to the
   * available pylons), rebuilding the mounts and toggling the rails mesh. Used
   * for the initial loadout and for live updates from the start-screen panel.
   * Intended for pre-flight configuration — it disposes all current missiles,
   * so don't call it mid-engagement with rounds in the air.
   */
  setAIM9Count(count) {
    // Clamp to available pylons. Math.floor (not `| 0`) so Infinity — the
    // "all pylons" sentinel — maps to the max rather than wrapping to 0.
    let n = Math.floor(count);
    if (!(n >= 0)) n = 0;
    if (n > AIM9.pylonNodes.length) n = AIM9.pylonNodes.length;
    this.armedAIM9 = n > 0;

    // A light loadout (≤ the number of single rails) mounts directly on the
    // single-rail empties with no launcher mesh; a heavier set uses the
    // double-rail pylons and shows the `_Missile_Rails` mesh.
    const useSingleRails = n > 0 && n <= AIM9.singlePylonNodes.length;
    const pylonNodes = useSingleRails ? AIM9.singlePylonNodes : AIM9.pylonNodes;

    const railsMesh = this.aircraftModel.getObjectByName(AIM9.railsMesh);
    if (railsMesh) railsMesh.visible = this.armedAIM9 && !useSingleRails;

    // Tear down existing mounts, then remount the first `n` pylons.
    for (let i = 0; i < this._missiles.length; i++) this._missiles[i].dispose();
    this._missiles.length = 0;

    if (n === 0 || !this._aim9Model) return;

    for (let i = 0; i < n; i++) {
      const pylonNode = this.aircraftModel.getObjectByName(pylonNodes[i]);
      if (!pylonNode) {
        console.warn(`WeaponSystem: AIM-9 pylon "${pylonNodes[i]}" not found in model`);
        continue;
      }
      // Clone the missile model per-mount (SkeletonUtils handles rigged meshes
      // too, in case the missile has any animation).
      const missileMesh = cloneSkeleton(this._aim9Model);
      missileMesh.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this._missiles.push(new AIM9Missile({ pylonNode, modelMesh: missileMesh, scene: this.scene }));
    }
  }

  /**
   * Set the mounted AGM-65 loadout to exactly `count` missiles (clamped to the
   * available pylons), rebuilding the mounts and toggling the rails mesh. Mirrors
   * {@link setAIM9Count}: a light pair mounts on the single-rail empties with no
   * launcher mesh, while a heavier set (4 or 6) uses the triple-rail pylons and
   * shows the `_Maverick_Rails` mesh. Pre-flight configuration only — disposes
   * all current Mavericks, so don't call it mid-engagement with rounds in the air.
   */
  setAGM65Count(count) {
    let n = Math.floor(count);
    if (!(n >= 0)) n = 0;
    if (n > AGM65.pylonNodes.length) n = AGM65.pylonNodes.length;

    // A light loadout (≤ the number of single rails) mounts directly on the
    // single-rail empties with no launcher mesh; a heavier set uses the
    // triple-rail pylons and shows the `_Maverick_Rails` mesh.
    const useSingleRails = n > 0 && n <= AGM65.singlePylonNodes.length;
    const pylonNodes = useSingleRails ? AGM65.singlePylonNodes : AGM65.pylonNodes;

    const railsMesh = this.aircraftModel.getObjectByName(AGM65.railsMesh);
    if (railsMesh) railsMesh.visible = n > 0 && !useSingleRails;

    for (let i = 0; i < this._mavericks.length; i++) this._mavericks[i].dispose();
    this._mavericks.length = 0;

    if (n === 0 || !this._agm65Model) return;

    for (let i = 0; i < n; i++) {
      const pylonNode = this.aircraftModel.getObjectByName(pylonNodes[i]);
      if (!pylonNode) {
        console.warn(`WeaponSystem: AGM-65 pylon "${pylonNodes[i]}" not found in model`);
        continue;
      }
      const missileMesh = cloneSkeleton(this._agm65Model);
      missileMesh.traverse((child) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;
        }
      });
      this._mavericks.push(new AGM65Missile({ pylonNode, modelMesh: missileMesh, scene: this.scene }));
    }
  }

  /** Cycle to the next weapon mode. */
  cycleMode() {
    this.mode = (this.mode + 1) % WEAPON_MODE_COUNT;
    this.firing = false;
  }

  /** Human-readable label for the current mode. */
  get modeLabel() {
    return WEAPON_LABELS[this.mode];
  }

  /** True when a weapon is selected (not NONE). */
  get hasWeaponSelected() {
    return this.mode !== WeaponMode.NONE;
  }

  /** Number of AIM-9s still mounted (available to fire). */
  get aim9MissilesRemaining() {
    let n = 0;
    for (let i = 0; i < this._missiles.length; i++) {
      if (this._missiles[i].state === MissileState.MOUNTED) n++;
    }
    return n;
  }

  /** Number of AGM-65s still mounted (available to fire). */
  get mavericksRemaining() {
    let n = 0;
    for (let i = 0; i < this._mavericks.length; i++) {
      if (this._mavericks[i].state === MissileState.MOUNTED) n++;
    }
    return n;
  }

  /** The next AGM-65 that would fire (first still-MOUNTED), or null. The
   *  MaverickSeeker reads its pylon node to position the TV camera. */
  get nextMountedMaverick() {
    for (let i = 0; i < this._mavericks.length; i++) {
      if (this._mavericks[i].state === MissileState.MOUNTED) return this._mavericks[i];
    }
    return null;
  }

  /** Current fire rate (rounds/sec), interpolated between start and max. */
  get currentFireRate() {
    return GAU8.fireRateStart + (GAU8.fireRateMax - GAU8.fireRateStart) * this._spinup;
  }

  /** Normalized spin-up (0 = start rate, 1 = max rate). */
  get fireRateNormalized() {
    return this._spinup;
  }

  /** Drain impact list (for future hit-effect spawning). */
  drainImpacts() {
    const out = this._impacts;
    this._impacts = [];
    return out;
  }

  /** Drain muzzle flash events (for particle spawning). */
  drainMuzzleFlashes() {
    const out = this._muzzleFlashes;
    this._muzzleFlashes = [];
    return out;
  }

  /** Drain missile launch events (for audio cueing). */
  drainMissileLaunches() {
    const out = this._missileLaunches;
    this._missileLaunches = [];
    return out;
  }

  /** Drain missile detonation events (for audio cueing). */
  drainMissileExplosions() {
    const out = this._missileExplosions;
    this._missileExplosions = [];
    return out;
  }

  // ── Per-frame update ────────────────────────────────────────────────────

  /**
   * @param {number} dt
   * @param {import('./Aircraft.js').default} aircraft
   * @param {import('./TerrainCollider.js').default} terrainCollider
   * @param {THREE.Vector3} wind — world-space wind velocity
   * @param {boolean} debug — whether to draw debug trails
   */
  update(dt, aircraft, terrainCollider, wind, debug) {
    // Toggle debug trails on/off (GAU8.debug overrides global debug flag)
    const showTrails = debug || GAU8.debug;
    if (showTrails !== this._debugActive) {
      this._debugActive = showTrails;
      if (!showTrails) this._removeAllTrails();
    }

    // ── AIM-9: target tracking + missile integration ─────────────────
    this._missileFireAccum = Math.max(0, this._missileFireAccum - dt);
    this._updateAIM9Tracking(aircraft);
    this._updateMissiles(dt, terrainCollider);

    // ── AGM-65: integrate fired Mavericks (aim/lock is driven externally) ──
    this._maverickFireAccum = Math.max(0, this._maverickFireAccum - dt);
    this._updateMavericks(dt, terrainCollider);

    // ── Spawn new bullets ─────────────────────────────────────────────
    const spinRate = 1 / Math.max(0.001, GAU8.spinupTime);
    if (this.firing && this.mode === WeaponMode.GAU8 && this.ammo > 0 && this.gunNode) {
      // Spin up toward max rate
      this._spinup = Math.min(1, this._spinup + spinRate * dt);

      // Recoil: continuous -barrel force. Split so total linear force is
      // preserved but the pitching torque from the gun's vertical CG offset
      // is attenuated (torqueScale of the natural moment arm).
      const torqueScale = 0.35;
      this.gunNode.getWorldPosition(_gunWorldPos);
      _gunWorldDir.set(0, 1, 0)
        .applyQuaternion(this.gunNode.getWorldQuaternion(_quat))
        .multiplyScalar(-GAU8.recoilForce);
      _gunWorldDir.multiplyScalar(torqueScale);
      aircraft.applyForceAtPoint(_gunWorldDir, _gunWorldPos);
      _gunWorldDir.multiplyScalar((1 - torqueScale) / torqueScale);
      aircraft.applyForceAtPoint(_gunWorldDir, aircraft.position);

      const fireInterval = 1 / this.currentFireRate;
      this._fireAccum += dt;
      while (this._fireAccum >= fireInterval && this.ammo > 0) {
        this._fireAccum -= fireInterval;
        this._spawnBullet(aircraft);
      }
    } else {
      // Spin down gradually; reset accumulator so bursts start immediately
      this._spinup = Math.max(0, this._spinup - spinRate * dt);
      this._fireAccum = 0;
    }

    // ── Integrate & collide alive bullets ─────────────────────────────
    const gravity = ENV.gravity;
    const maxLife = GAU8.maxLifetime;
    const windX = wind.x, windY = wind.y, windZ = wind.z;

    for (let i = 0; i < this.poolSize; i++) {
      if (!this.alive[i]) continue;

      // Age
      this.age[i] += dt;
      if (this.age[i] > maxLife) {
        this._killBullet(i);
        continue;
      }

      // Save old position for raycast
      const ox = this.posX[i];
      const oy = this.posY[i];
      const oz = this.posZ[i];

      // Wind-relative velocity for drag
      const vrx = this.velX[i] - windX;
      const vry = this.velY[i] - windY;
      const vrz = this.velZ[i] - windZ;
      const speed2 = vrx * vrx + vry * vry + vrz * vrz;
      const speed = Math.sqrt(speed2);

      // Drag acceleration: -DRAG_K * |v_rel| * v_rel_hat = -DRAG_K * v_rel (scalar * direction)
      // Combined: a_drag = -DRAG_K * speed * v_rel_unit = -DRAG_K * v_rel (when multiplied by speed/speed)
      // Actually: F_drag = 0.5 * rho * Cd * A * |v|^2, a = F/m = DRAG_K * |v|^2
      // Direction: opposing v_rel, so a_drag = -DRAG_K * speed * v_rel
      let ax = 0, ay = -gravity, az = 0;
      if (speed > 0.01) {
        const dragAccel = DRAG_K * speed; // DRAG_K * |v_rel| — multiplied by unit vector below
        ax -= dragAccel * vrx;
        ay -= dragAccel * vry;
        az -= dragAccel * vrz;
      }

      // Semi-implicit Euler: update velocity first, then position
      this.velX[i] += ax * dt;
      this.velY[i] += ay * dt;
      this.velZ[i] += az * dt;

      this.posX[i] += this.velX[i] * dt;
      this.posY[i] += this.velY[i] * dt;
      this.posZ[i] += this.velZ[i] * dt;

      // ── Collision: swept segment from old → new position ────────
      const dx = this.posX[i] - ox;
      const dy = this.posY[i] - oy;
      const dz = this.posZ[i] - oz;
      const segLen = Math.sqrt(dx * dx + dy * dy + dz * dz);

      // Target hit-volumes (run before terrain so an enemy below the bullet
      // path is preferred over the ground beneath it).
      if (segLen > 0.001 && this._targets) {
        let bestHit = null;
        let bestEntity = null;
        for (let t = 0; t < this._targets.length; t++) {
          const ent = this._targets[t];
          const hv = ent.hitVolume;
          if (!hv) continue;
          const h = hv.intersectsSegment(ox, oy, oz, dx, dy, dz);
          if (h && (!bestHit || h.t < bestHit.t)) {
            bestHit = h;
            bestEntity = ent;
          }
        }
        if (bestHit) {
          this.posX[i] = bestHit.x;
          this.posY[i] = bestHit.y;
          this.posZ[i] = bestHit.z;

          this._impacts.push({ x: bestHit.x, y: bestHit.y, z: bestHit.z, type: 'target' });
          const damage = Math.max(0, GAU8.damage + gaussRand() * GAU8.damageVariance);
          bestEntity.onProjectileHit?.(bestHit.x, bestHit.y, bestHit.z, damage, 'gau8');
          this.stats?.recordGunHit();

          if (this._debugActive) {
            this._appendTrailPoint(i, bestHit.x, bestHit.y, bestHit.z);
          }

          this._killBullet(i);
          continue;
        }
      }

      if (segLen > 0.001 && terrainCollider) {
        const hit = terrainCollider.raycast(
          ox, oy, oz,
          dx, dy, dz,
          segLen,
          segLen, // single step — segment is short enough
        );
        if (hit) {
          // Snap bullet to impact point
          this.posX[i] = hit.x;
          this.posY[i] = hit.y;
          this.posZ[i] = hit.z;

          this._impacts.push({ x: hit.x, y: hit.y, z: hit.z, type: 'terrain' });

          // Add final trail point at impact before killing
          if (this._debugActive) {
            this._appendTrailPoint(i, hit.x, hit.y, hit.z);
          }

          this._killBullet(i);
          continue;
        }
      }

      // Quick below-ground safety (in case heightmap isn't loaded)
      if (this.posY[i] < -10) {
        this._killBullet(i);
        continue;
      }

      // ── Debug trail ─────────────────────────────────────────────
      if (this._debugActive) {
        this._appendTrailPoint(i, this.posX[i], this.posY[i], this.posZ[i]);
      }
    }
  }

  // ── Bullet spawning ─────────────────────────────────────────────────────

  _spawnBullet(aircraft) {
    // Find a free slot
    let slot = -1;
    for (let i = 0; i < this.poolSize; i++) {
      if (!this.alive[i]) { slot = i; break; }
    }
    if (slot === -1) return; // pool full — drop the round

    // Gun world position
    this.gunNode.getWorldPosition(_gunWorldPos);

    // Gun barrel points along local -Y in the model (exported Y-up).
    // Transform that axis into world space via the node's world rotation.
    _gunWorldDir.set(0, 1, 0)
      .applyQuaternion(this.gunNode.getWorldQuaternion(_quat));

    // Apply dispersion — two independent Gaussian offsets on perpendicular axes
    if (DISPERSION_SIGMA > 0) {
      const dYaw = gaussRand() * DISPERSION_SIGMA;
      const dPitch = gaussRand() * DISPERSION_SIGMA;
      // Build a perpendicular frame around the barrel direction
      // Pick a non-parallel reference to cross with
      const ref = Math.abs(_gunWorldDir.y) < 0.9 ? 0 : 1; // 0=up, 1=right
      const rx = ref === 0 ? 0 : 1, ry = ref === 0 ? 1 : 0, rz = 0;
      // tangent = barrel × ref
      const tx = _gunWorldDir.y * rz - _gunWorldDir.z * ry;
      const ty = _gunWorldDir.z * rx - _gunWorldDir.x * rz;
      const tz = _gunWorldDir.x * ry - _gunWorldDir.y * rx;
      // bitangent = barrel × tangent
      const bx = _gunWorldDir.y * tz - _gunWorldDir.z * ty;
      const by = _gunWorldDir.z * tx - _gunWorldDir.x * tz;
      const bz = _gunWorldDir.x * ty - _gunWorldDir.y * tx;
      _gunWorldDir.x += tx * dYaw + bx * dPitch;
      _gunWorldDir.y += ty * dYaw + by * dPitch;
      _gunWorldDir.z += tz * dYaw + bz * dPitch;
      _gunWorldDir.normalize();
    }

    // Initial velocity = aircraft velocity + muzzle velocity along barrel
    const mv = GAU8.muzzleVelocity;
    this.posX[slot] = _gunWorldPos.x;
    this.posY[slot] = _gunWorldPos.y;
    this.posZ[slot] = _gunWorldPos.z;
    this.velX[slot] = aircraft.velocity.x + _gunWorldDir.x * mv;
    this.velY[slot] = aircraft.velocity.y + _gunWorldDir.y * mv;
    this.velZ[slot] = aircraft.velocity.z + _gunWorldDir.z * mv;
    this.age[slot]  = 0;
    this.alive[slot] = 1;
    this.aliveCount++;
    this.stats?.recordGunFired();

    if (!this.unlimitedAmmo) this.ammo--;

    // Emit muzzle flash event
    this._muzzleFlashes.push({
      x: _gunWorldPos.x, y: _gunWorldPos.y, z: _gunWorldPos.z,
      vx: aircraft.velocity.x, vy: aircraft.velocity.y, vz: aircraft.velocity.z,
      dx: _gunWorldDir.x, dy: _gunWorldDir.y, dz: _gunWorldDir.z,
    });

    // Reset trail for this slot
    this._resetTrail(slot);
    if (this._debugActive) {
      this._ensureTrailLine(slot);
      this._appendTrailPoint(slot, _gunWorldPos.x, _gunWorldPos.y, _gunWorldPos.z);
    }
  }

  // ── Bullet death ────────────────────────────────────────────────────────

  _killBullet(i) {
    this.alive[i] = 0;
    this.aliveCount--;

    // Remove trail after a brief moment — for now, remove immediately
    this._removeTrail(i);
  }

  // ── Debug trail management ──────────────────────────────────────────────

  _ensureTrailLine(i) {
    if (this._trailLines[i]) return;

    const maxPts = GAU8.trailMaxPoints;
    const positions = new Float32Array(maxPts * 3);
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setDrawRange(0, 0);

    const line = new THREE.Line(geom, this._trailMaterial);
    line.frustumCulled = false;
    this._trailLines[i] = line;
    this._trailCounts[i] = 0;
    this.scene.add(line);
  }

  _appendTrailPoint(i, x, y, z) {
    const line = this._trailLines[i];
    if (!line) return;

    const count = this._trailCounts[i];
    if (count >= GAU8.trailMaxPoints) return; // trail full

    const attr = line.geometry.getAttribute('position');
    attr.array[count * 3] = x;
    attr.array[count * 3 + 1] = y;
    attr.array[count * 3 + 2] = z;
    attr.needsUpdate = true;

    this._trailCounts[i] = count + 1;
    line.geometry.setDrawRange(0, count + 1);
  }

  _resetTrail(i) {
    this._trailCounts[i] = 0;
    const line = this._trailLines[i];
    if (line) {
      line.geometry.setDrawRange(0, 0);
    }
  }

  _removeTrail(i) {
    const line = this._trailLines[i];
    if (!line) return;
    this.scene.remove(line);
    line.geometry.dispose();
    this._trailLines[i] = null;
    this._trailCounts[i] = 0;
  }

  _removeAllTrails() {
    for (let i = 0; i < this.poolSize; i++) {
      this._removeTrail(i);
    }
  }

  // ── AIM-9 ───────────────────────────────────────────────────────────────

  /**
   * One-shot trigger from the input layer (LMB click in AIM-9 mode).
   * Picks the first MOUNTED missile, locks it onto the currently tracked
   * enemy, and fires. Rate-limited by AIM9.fireInterval.
   *
   * @param {import('../aircraft/Aircraft.js').default} aircraft
   * @returns {boolean} true if a missile was launched.
   */
  tryFireAIM9(aircraft) {
    if (this.mode !== WeaponMode.AIM9) return false;
    if (!this.trackedTarget) return false;
    if (this._missileFireAccum > 0) return false;

    for (let i = 0; i < this._missiles.length; i++) {
      const m = this._missiles[i];
      if (m.state === MissileState.MOUNTED) {
        m.fire(this.trackedTarget, aircraft.velocity);
        aircraft.launchTrauma = 1; // brief cockpit shake from the rocket firing
        this._missileFireAccum = AIM9.fireInterval;
        this.stats?.recordSidewinderFired();
        this._missileLaunches.push({ x: m.position.x, y: m.position.y, z: m.position.z });
        if (this.smokeTrailSystem) {
          m.smokeTrailHandle = this.smokeTrailSystem.spawn(SMOKE_TRAILS.presets.aim9);
        }
        // Launch warning — entity decides whether to act on it (e.g. MIG
        // dispenses flares; Mi-8 has no MAWS and ignores it).
        this.trackedTarget.onAIM9Launched?.();
        return true;
      }
    }
    return false;
  }

  /** Release a missile's smoke trail handle so the ribbon ages out cleanly. */
  _releaseMissileTrail(m) {
    if (!this.smokeTrailSystem) return;
    if (m.smokeTrailHandle === undefined || m.smokeTrailHandle < 0) return;
    this.smokeTrailSystem.release(m.smokeTrailHandle);
    m.smokeTrailHandle = -1;
  }

  /**
   * Recompute tracked target each frame: nearest enemy within gimbal cone
   * and acquire range. Only runs while AIM-9 mode is selected — otherwise
   * trackedTarget stays cleared so HUD/lock indicators don't linger.
   */
  _updateAIM9Tracking(aircraft) {
    if (this.mode !== WeaponMode.AIM9 || !this._targets || this._targets.length === 0) {
      this.trackedTarget = null;
      return;
    }

    const fwd = aircraft.forwardVector;
    const ax = aircraft.position.x;
    const ay = aircraft.position.y;
    const az = aircraft.position.z;
    const cosGimbal = Math.cos(AIM9.gimbalLimitDeg * DEG2RAD);
    const maxRange = AIM9.acquireRange;
    const maxRangeSq = maxRange * maxRange;

    let best = null;
    let bestRangeSq = Infinity;
    for (let i = 0; i < this._targets.length; i++) {
      const ent = this._targets[i];
      if (!ent || ent.dead || ent.destroyed) continue;
      if (ent.isGroundTarget) continue;  // AIM-9 IR seeker — air targets only
      // Use flight.position when available (helicopter shape), else hitVolume center.
      if (ent.flight) {
        _targetPos.copy(ent.flight.position);
      } else if (ent.hitVolume) {
        _targetPos.copy(ent.hitVolume.center);
      } else {
        continue;
      }

      _toTarget.set(_targetPos.x - ax, _targetPos.y - ay, _targetPos.z - az);
      const distSq = _toTarget.lengthSq();
      if (distSq > maxRangeSq) continue;

      const dist = Math.sqrt(distSq);
      if (dist < 1) continue;
      const dot = (_toTarget.x * fwd.x + _toTarget.y * fwd.y + _toTarget.z * fwd.z) / dist;
      if (dot < cosGimbal) continue;  // outside gimbal cone

      if (distSq < bestRangeSq) {
        bestRangeSq = distSq;
        best = ent;
      }
    }
    this.trackedTarget = best;
  }

  /**
   * Try to fire the next mounted AGM-65 at a player-designated target. Called by
   * the MaverickSeeker (LMB while aiming, in range). Locks the supplied moving
   * entity if one was designated, else flies to the fixed ground point.
   *
   * @param {import('../aircraft/Aircraft.js').default} aircraft
   * @param {object|null} targetEntity — enemy to track (or null for a ground shot)
   * @param {THREE.Vector3} targetPoint — fixed world aim point (fallback / ground shot)
   * @returns {{ fired: boolean, remaining: number }}
   */
  tryFireMaverick(aircraft, targetEntity, targetPoint) {
    if (this.mode !== WeaponMode.AGM65) return { fired: false, remaining: this.mavericksRemaining };
    if (this._maverickFireAccum > 0) return { fired: false, remaining: this.mavericksRemaining };

    const m = this.nextMountedMaverick;
    if (!m) return { fired: false, remaining: 0 };

    m.fire(targetEntity || null, targetPoint, aircraft.velocity);
    aircraft.launchTrauma = 1; // brief cockpit shake from the rocket firing
    this._maverickFireAccum = AGM65.fireInterval;
    this.stats?.recordMaverickFired();
    this._missileLaunches.push({ x: m.position.x, y: m.position.y, z: m.position.z });
    if (this.smokeTrailSystem) {
      m.smokeTrailHandle = this.smokeTrailSystem.spawn(SMOKE_TRAILS.presets.agm65);
    }
    return { fired: true, remaining: this.mavericksRemaining };
  }

  /**
   * Tick all FIRED AIM-9 missiles, run swept hit-detection vs targets and
   * terrain, drain hits into _impacts (shared with bullet impact pipeline),
   * and prune DEAD missiles.
   */
  _updateMissiles(dt, terrainCollider) {
    for (let i = 0; i < this._missiles.length; i++) {
      this._advanceFiredMissile(this._missiles[i], dt, terrainCollider, AIM9.damage, 'aim9');
    }
  }

  /** Tick all FIRED AGM-65 Mavericks through the same hit pipeline. */
  _updateMavericks(dt, terrainCollider) {
    for (let i = 0; i < this._mavericks.length; i++) {
      this._advanceFiredMissile(this._mavericks[i], dt, terrainCollider, AGM65.damage, 'agm65');
    }
  }

  /**
   * Advance one FIRED missile (AIM-9 or AGM-65): integrate it, extend its smoke
   * trail, and run swept hit-detection against target hit-volumes (preferred)
   * and terrain. Shared by both missile types — they expose the same interface.
   */
  _advanceFiredMissile(m, dt, terrainCollider, damage, kind) {
    if (m.state !== MissileState.FIRED) return;

    m.update(dt);
    if (m.state !== MissileState.FIRED) {
      // Killed inside update() — proximity-fuse intercept (hit) or lifetime
      // expiry (miss); the detonation flag tells which.
      this._releaseMissileTrail(m);
      if (m.detonation) {
        this.stats?.recordMissileResult(kind, m.detonation.isAircraftHit);
        this._explodeMissile(m.position.x, m.position.y, m.position.z, m.detonation.isAircraftHit);
        m.detonation = null;
      }
      return;
    }

    // Smoke trail — extend the per-missile ribbon to the current head pos.
    if (this.smokeTrailSystem && m.smokeTrailHandle !== undefined && m.smokeTrailHandle >= 0) {
      this.smokeTrailSystem.advance(
        m.smokeTrailHandle,
        m.position.x, m.position.y, m.position.z,
        m.velocity.x, m.velocity.y, m.velocity.z,
      );
    }

    // Swept segment from previous → current position for hit tests.
    _segDelta.set(
      m.position.x - m.prevX,
      m.position.y - m.prevY,
      m.position.z - m.prevZ,
    );
    const segLen = _segDelta.length();
    if (segLen < 0.001) return;

    // Target hit-volumes — preferred over terrain so a tracked enemy
    // hugging the ground is still hit instead of the dirt under it.
    //
    // The slabs test is run in the target's body frame: shift the segment
    // start by +vT·dt so it expresses the missile's relative motion against
    // the post-update OBB. Without this the OBB can slide past a missile
    // segment that actually crossed the body during the frame — at MiG
    // closing speeds (~1000 m/s relative, ~17 m / frame) and a 2 m hitbox
    // this is a real tunneling case. The hit point t is parametric, so
    // we map it back onto the missile's actual world segment for the
    // explosion location.
    let hit = null;
    let hitEntity = null;
    if (this._targets) {
      for (let i2 = 0; i2 < this._targets.length; i2++) {
        const ent = this._targets[i2];
        const hv = ent?.hitVolume;
        if (!hv) continue;
        const tvx = ent.flight ? ent.flight.velocity.x * dt : 0;
        const tvy = ent.flight ? ent.flight.velocity.y * dt : 0;
        const tvz = ent.flight ? ent.flight.velocity.z * dt : 0;
        const ox = m.prevX + tvx;
        const oy = m.prevY + tvy;
        const oz = m.prevZ + tvz;
        const h = hv.intersectsSegment(
          ox, oy, oz,
          _segDelta.x - tvx, _segDelta.y - tvy, _segDelta.z - tvz,
        );
        if (h && (!hit || h.t < hit.t)) {
          const tHit = h.t;
          hit = {
            t: tHit,
            x: m.prevX + _segDelta.x * tHit,
            y: m.prevY + _segDelta.y * tHit,
            z: m.prevZ + _segDelta.z * tHit,
          };
          hitEntity = ent;
        }
      }
    }

    if (hit) {
      this._impacts.push({ x: hit.x, y: hit.y, z: hit.z, type: 'target' });
      hitEntity.onProjectileHit?.(hit.x, hit.y, hit.z, damage, kind);
      this.stats?.recordMissileResult(kind, true);
      this._explodeMissile(hit.x, hit.y, hit.z, true);
      this._releaseMissileTrail(m);
      m.kill();
      return;
    }

    if (terrainCollider) {
      const th = terrainCollider.raycast(
        m.prevX, m.prevY, m.prevZ,
        _segDelta.x, _segDelta.y, _segDelta.z,
        segLen,
        segLen,
      );
      if (th) {
        this._impacts.push({ x: th.x, y: th.y, z: th.z, type: 'terrain' });
        this.stats?.recordMissileResult(kind, false);
        this._explodeMissile(th.x, th.y, th.z, false);
        this._releaseMissileTrail(m);
        m.kill();
      }
    }
  }

  /**
   * Spawn the missile detonation effect. Direct target impacts get full debris;
   * terrain impacts and self-destructs suppress debris (no airframe to shred).
   */
  _explodeMissile(x, y, z, isAircraftHit) {
    this._missileExplosions.push({ name: 'Explosion_AIM9', x, y, z });
    if (!this.particleSystem) return;
    this.particleSystem.spawnExplosion(x, y, z, isAircraftHit ? undefined : { debrisCount: 0 });
  }

  // ── Cleanup ─────────────────────────────────────────────────────────────

  dispose() {
    this._removeAllTrails();
    this._trailMaterial.dispose();
    for (let i = 0; i < this._missiles.length; i++) {
      this._missiles[i].dispose();
    }
    this._missiles.length = 0;
    for (let i = 0; i < this._mavericks.length; i++) {
      this._mavericks[i].dispose();
    }
    this._mavericks.length = 0;
  }
}
